
import { useEffect, useState, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Checkbox,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { TreeDropdown, type TreeDropdownRef } from '@/components/forms/TreeDropdown'
import { createNavigationSchema, updateNavigationSchema, type CreateNavigationFormData, type UpdateNavigationFormData } from '@/lib/validations/auth'

interface NavigationFormProps {
  navigationId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function NavigationForm({ navigationId, onSuccess, onCancel }: NavigationFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const navigationRef = useRef<TreeDropdownRef>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!navigationId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateNavigationFormData | UpdateNavigationFormData>({
    resolver: zodResolver(isEditMode ? updateNavigationSchema : createNavigationSchema),
    defaultValues: {
      active: true,
      show_in_menu: true,
    },
  })

  const activeValue = watch('active')
  const showInMenuValue = watch('show_in_menu')

  const loadOptions = useCallback((parentId: number | null, callback: (data: any[]) => void) => {
    const url = `/options/navigations${parentId ? `/${parentId}` : ''}`
    setTimeout(() => {
      apiClient
        .get(url)
        .then(({ data }) => {
          const navData = data?.data || data || []
          callback(Array.isArray(navData) ? navData : [])
        })
        .catch((error: any) => {
          showError(error)
          callback([])
        })
    }, 500)
  }, [showError])

  // Fetch navigation data for edit mode
  useEffect(() => {
    if (isEditMode && navigationId) {
      setIsLoading(true)
      apiClient
        .get(`/system-settings/navigation/${navigationId}`)
        .then(({ data }) => {
          const navData = data?.data || data || {}

          // Extract parent_id
          let parentId: number | null = null
          if (navData.parent_id) {
            parentId = navData.parent_id
          } else if (navData.parent_navigation?.id) {
            parentId = navData.parent_navigation.id
          }

          // Reset form with all values
          reset({
            name: navData.name || '',
            slug: navData.slug || '',
            icon: navData.icon || '',
            parent_id: parentId,
            active: navData.active !== undefined ? Boolean(navData.active) : true,
            show_in_menu: navData.show_in_menu !== undefined ? Boolean(navData.show_in_menu) : true,
          })

          // Set parent navigation value for TreeDropdown
          if (navData.parent_navigation) {
            // TreeDropdown will handle the value from the form's parent_id
          }
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isEditMode, navigationId, reset, showError])

  const onSubmit = async (data: CreateNavigationFormData | UpdateNavigationFormData) => {
    setIsSubmitting(true)

    try {
      const parentValue = navigationRef.current?.getValue()
      let parentId: number | null = null

      if (parentValue) {
        if (typeof parentValue === 'object' && parentValue !== null && 'id' in parentValue) {
          parentId = (parentValue as { id: number | null }).id
        } else if (typeof parentValue === 'string') {
          try {
            const parsed = JSON.parse(parentValue)
            parentId = parsed.id || null
          } catch {
            // If not JSON, try to parse as number
            parentId = parentValue ? parseInt(parentValue) : null
          }
        }
      }

      const payload = {
        name: data.name,
        slug: data.slug,
        icon: data.icon || '',
        parent_id: parentId,
        active: data.active,
        show_in_menu: data.show_in_menu,
      }

      if (isEditMode) {
        await apiClient.put(`/system-settings/navigation/${navigationId}`, payload)
        showSuccess('Success', 'Navigation updated successfully')
      } else {
        await apiClient.post('/system-settings/navigation', payload)
        showSuccess('Success', 'Navigation created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        setTimeout(() => navigate('/system-settings/navigation'), 2000)
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/system-settings/navigation')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading navigation...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        {/* Name Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input
              {...register('name')}
              placeholder="e.g. Dashboard, Users, Settings"
            />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The name is how it appears on your site.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Slug Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Slug <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.slug}>
            <Input
              {...register('slug')}
              placeholder="e.g. dashboard, users, settings"
            />
            <FormErrorMessage>{errors.slug?.message}</FormErrorMessage>
            <FormHelperText>The slug is how it appears on your site.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Icon Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Icon</FormLabel>
          <FormControl isInvalid={!!errors.icon}>
            <Input
              {...register('icon')}
              placeholder="e.g. home, users, cog-6-tooth"
            />
            <FormErrorMessage>{errors.icon?.message}</FormErrorMessage>
            <FormHelperText>The icon name (e.g. home, users, cog-6-tooth).</FormHelperText>
          </FormControl>
        </Grid>

        {/* Parent Navigation Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Parent Navigation</FormLabel>
          <FormControl>
            <TreeDropdown
              ref={navigationRef}
              values={watch('parent_id') ? JSON.stringify({ id: watch('parent_id') }) : null}
              loadOptions={loadOptions}
              placeholder="Select Navigation"
              onChange={(value) => {
                try {
                  const parsed = JSON.parse(value)
                  setValue('parent_id', parsed.id, { shouldValidate: true })
                } catch {
                  setValue('parent_id', null, { shouldValidate: true })
                }
              }}
            />
            <FormHelperText>Navigations, unlike tags, can have a hierarchy.</FormHelperText>
          </FormControl>
        </Grid>

        {/* Active Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={activeValue}
              onChange={(e) => setValue('active', e.target.checked)}
            >
              Active
            </Checkbox>
          </FormControl>
        </Grid>

        {/* Show in Menu Field */}
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Show in menu</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={showInMenuValue}
              onChange={(e) => setValue('show_in_menu', e.target.checked)}
            >
              Show in menu
            </Checkbox>
          </FormControl>
        </Grid>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button
              variant="outline"
              onClick={handleCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Navigation'}
            {isSubmitting && <Text as="span" ml={2} fontSize="sm">...</Text>}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
